import { FunctionComponent, useEffect, useMemo } from 'react';
import { Stack, Typography } from '@mui/material';
import { TextField } from '@nps.today/component-library';
import { CustomAutocomplete } from 'components/util/CustomAutocomplete';
import { ChildTenant, ChildTenantAccessUser, Claim } from '../../../@types/organization';
import { getAssignableClaims } from 'redux/slices/organization';
import { getOrganizationUsers } from 'redux/slices/user';
import { getPermissions } from 'redux/slices/permissions';
import { RootState, useSelector } from 'redux/store';
import useCustomDispatch from 'redux/dispatch';
import useAuth from 'hooks/useAuth';
import { isAccessible, rules } from 'access';
import { useTranslator } from 'translation/useTranslator';
import UserAccessFields from 'pages/organizations/components/UserAccessFields';

const MAX_PAGE_SIZE_ALLOWED_BY_API = 100;

export type PickableUser = {
  userId: string;
  name?: string | null;
  email: string;
};

interface ChildTenantAccessFormProps {
  childTenant: ChildTenant;
  usersWithAccess: ChildTenantAccessUser[];
  selectedUsers: PickableUser[];
  role: string;
  claims: Claim[];
  showPermissions: boolean;
  onSelectedUsersChange: (users: PickableUser[]) => void;
  onRoleChange: (role: string, claims: Claim[]) => void;
  onClaimsChange: (claims: Claim[]) => void;
}

const ChildTenantAccessForm: FunctionComponent<ChildTenantAccessFormProps> = ({
  childTenant,
  usersWithAccess,
  selectedUsers,
  role,
  claims,
  showPermissions,
  onSelectedUsersChange,
  onRoleChange,
  onClaimsChange
}) => {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const { user } = useAuth();
  const { users, usersTotal } = useSelector((state: RootState) => state.user);
  const { assignableClaims } = useSelector((state: RootState) => state.organization);
  const { assignableRoles, isLoading: isPermissionsLoading } = useSelector(
    (state: RootState) => state.permissions
  );

  const canAssignClaims = isAccessible(rules.claimAssignment.any.claims ?? [], user?.claims ?? []);

  const capabilities = useMemo(
    () => ({
      isAiFeedbackEnabled: childTenant.isAiFeedbackEnabled ?? false,
      assignablePermissionClaims: assignableClaims?.claims ?? null
    }),
    [childTenant.isAiFeedbackEnabled, assignableClaims]
  );

  useEffect(() => {
    customDispatch({ action: getPermissions, disableSuccessMessage: true });
    customDispatch({
      action: getAssignableClaims,
      actionParameters: childTenant.id,
      disableSuccessMessage: true
    });

    customDispatch({
      action: getOrganizationUsers,
      actionParameters: { skip: 0, take: MAX_PAGE_SIZE_ALLOWED_BY_API },
      disableSuccessMessage: true
    });
  }, [childTenant.id, customDispatch]);

  const alreadyHasAccess = useMemo(
    () => new Set(usersWithAccess.map((accessUser) => accessUser.userId)),
    [usersWithAccess]
  );

  const options: PickableUser[] = useMemo(
    () =>
      users.map((organizationUser: any) => ({
        userId: organizationUser.userId,
        name: organizationUser.name,
        email: organizationUser.email
      })),
    [users]
  );

  return (
    <UserAccessFields
      dataTestId="child-tenant-access-form"
      role={role}
      claims={claims}
      capabilities={capabilities}
      assignableRoles={assignableRoles}
      canAssignClaims={canAssignClaims}
      showPermissions={showPermissions}
      disabled={isPermissionsLoading}
      onRoleChange={onRoleChange}
      onClaimsChange={onClaimsChange}
      userField={
        <CustomAutocomplete
          dataTestId="child-tenant-access-form-users"
          multiple
          options={options}
          value={selectedUsers}
          onChange={(_event, value) => onSelectedUsersChange(value as PickableUser[])}
          getOptionLabel={(option) => option.name || option.email}
          isOptionEqualToValue={(option, value) => option.userId === value.userId}
          getOptionDisabled={(option) => alreadyHasAccess.has(option.userId)}
          filterOptions={(available, state) => {
            const query = state.inputValue.trim().toLowerCase();
            if (!query) {
              return available;
            }
            return available.filter(
              (option) =>
                (option.name ?? '').toLowerCase().includes(query) ||
                option.email.toLowerCase().includes(query)
            );
          }}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props as any;
            return (
              <li key={key} {...optionProps}>
                <Stack>
                  <Typography variant="body2">{option.name || option.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {alreadyHasAccess.has(option.userId)
                      ? translator.alreadyHasChildTenantAccess()
                      : option.email}
                  </Typography>
                </Stack>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              dataTestId="child-tenant-access-form-users-input"
              label={translator.findUsersInOrganization()}
              helperText={
                usersTotal > options.length
                  ? translator.showingFirstUsers(`${options.length}`, `${usersTotal}`)
                  : undefined
              }
              {...params}
            />
          )}
        />
      }
    />
  );
};

export default ChildTenantAccessForm;
